import { ChangeAttrName } from '@common/flare/change-attr-name.enum';
import { SimpleDomNode } from '@common/html/simple-dom/node';
import { ChangeType } from '@common/prosemirror/tracked-changes/change-type.enum';
import { TrackedChange } from '@common/prosemirror/tracked-changes/tracked-change.type';
import { Mark, ProseMirrorNode } from 'prosemirror-model';

/**
 * Extracts tracked changes from Flare XML so they can be injected into a ProseMirror document.
 */
export class TrackedChangesImporter {
  private changeData: Dictionary<TrackedChange>;

  public extractChangeData(changeDataNode: SimpleDomNode) {
    const changeData: Dictionary<TrackedChange> = {
      // Initialize with an untracked change. All untracked changes have the same change data and do not appear in <MadCap:changeData>
      // This makes the data for an untracked change available when an untracked change is encountered in the <body> later in this function
      [ChangeType.Untracked]: {
        changeType: ChangeType.Untracked,
        id: ChangeType.Untracked
      }
    };

    // Store the <MadCap:changeData> info in map changeId => data
    for (let i = 0, length = changeDataNode.children.length; i < length; i += 1) {
      const changeElement = changeDataNode.children[i];
      let changeContent: string;
      let changeType: ChangeType;

      switch (changeElement.nodeName) {
        case 'MadCap:AddChange':
          changeType = ChangeType.Add;
          break;
        case 'MadCap:RemoveChange':
          changeType = ChangeType.Remove;
          break;
        case 'MadCap:ReplaceChange':
          changeType = ChangeType.Replace;
          changeContent = changeElement.innerHTML;
          break;
        case 'MadCap:BindChange':
          changeType = ChangeType.Bind;
          break;
        case 'MadCap:UnbindChange':
          changeType = ChangeType.Unbind;
          changeContent = changeElement.innerHTML;
          break;
        case 'MadCap:AttributesChange':
          changeType = ChangeType.Attributes;
          break;
        default:
          throw new Error('Unknown change element found. ' + changeElement.nodeName);
      }

      const change: TrackedChange = {};
      this.setChangeProperty(change, 'attribute', changeElement.getAttribute(ChangeAttrName.Attribute));
      this.setChangeProperty(change, 'changeContent', changeContent);
      this.setChangeProperty(change, 'changeType', changeType);
      this.setChangeProperty(change, 'creatorCentralUserId', changeElement.getAttribute(ChangeAttrName.CreatorCentralUserId));
      this.setChangeProperty(change, 'id', changeElement.getAttribute('MadCap:id'));
      this.setChangeProperty(change, 'initials', changeElement.getAttribute(ChangeAttrName.Initials));
      this.setChangeProperty(change, 'timestamp', changeElement.getAttribute(ChangeAttrName.Timestamp));
      this.setChangeProperty(change, 'userName', changeElement.getAttribute(ChangeAttrName.UserName));
      this.setChangeProperty(change, 'value', changeElement.getAttribute(ChangeAttrName.Value));

      changeData[changeElement.getAttribute('MadCap:id')] = change;
    }

    this.changeData = changeData;
  }

  public injectChangeData(node: ProseMirrorNode) {
    if (!this.changeData) {
      return;
    }

    node.descendants(node => {
      this.updateChangeAttrs(node);

      node.marks.forEach(mark => {
        if (mark.type.name === 'madcapchange') {
          this.updateChangeAttrs(mark);
        }
      });
    });
  }

  private updateChangeAttrs(nodeOrMark: ProseMirrorNode | Mark) {
    const changeIds = (nodeOrMark.attrs[ChangeAttrName.ChangeIds] as string)?.split(',').map(changeId => changeId.trim());

    if (changeIds?.length > 0) {
      const changeList = changeIds.map(changeId => this.changeData[changeId]).filter(change => !!change);
      if (changeList?.length > 0) {
        // Cast as any to set a readonly property
        (nodeOrMark.attrs as any).changeList = changeList;
      }
    }
  }

  /**
   * Sets a property on a tracked change if and only if the value is not undefined.
   * This is to prevent extraneous properties from being in the doc when exported to JSON.
   * This also prevents an issue where comparing tracked change attributes would fail when comparing an undefined property with a missing property.
   * @param change The tracked change to set the property on.
   * @param key The property to set.
   * @param value The value to set the property to.
   */
  private setChangeProperty(change: TrackedChange, key: keyof TrackedChange, value: any) {
    if (value) {
      // Cast to `any`: TrackedChange's optional properties span multiple, mutually-incompatible enum/string
      // types, which makes a generic `keyof TrackedChange` indexed write resolve to `never` for TypeScript's
      // homomorphic mapped-type check even though `value` is deliberately untyped here.
      (change as any)[key] = value;
    }
  }
}
